import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  NotificationsActiveOutlined,
  EmailOutlined,
  MailOutlined,
  PhoneIphoneOutlined,
  InfoOutlined,
  CheckCircleRounded,
  CancelRounded,
} from '@mui/icons-material';
import { type NotificationChannel } from '../../../lib/api/notification.api';
import {
  useNotificationPreferences,
  ALL_CHANNELS,
  ALL_NOTIFICATION_TYPES,
} from '../../../hooks/use-notification-preferences';
import { PortalLoading } from '../PortalShell';

function channelIcon(ch: NotificationChannel): JSX.Element {
  if (ch === 'in-app') return <NotificationsActiveOutlined fontSize="small" />;
  if (ch === 'email') return <EmailOutlined fontSize="small" />;
  return <PhoneIphoneOutlined fontSize="small" />;
}

export function PortalNotificationsSettingsPage(): JSX.Element {
  const { t } = useTranslation('notifications');
  const {
    disabledChannels,
    optedOutTypes,
    loading,
    saving,
    error,
    savedMsg,
    dirty,
    toggleChannel,
    toggleType,
    save,
    enablePush,
    setError,
    setSavedMsg,
  } = useNotificationPreferences();

  if (loading) return <PortalLoading />;

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 820 }}>
      <Box>
        <Typography variant="h4" fontWeight={700} color="#e6edf3">
          {t('prefs.titlePortal')}
        </Typography>
        <Typography variant="body2" color="#8b949e">
          {t('prefs.subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {savedMsg && (
        <Alert severity="success" onClose={() => setSavedMsg(null)}>
          {savedMsg}
        </Alert>
      )}

      {/* Channels card */}
      <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={700} color="#e6edf3" mb={0.5}>
          {t('prefs.channelsCard.title')}
        </Typography>
        <Typography variant="caption" color="#8b949e" sx={{ display: 'block', mb: 2 }}>
          {t('prefs.channelsCard.hint')}
        </Typography>
        <Stack divider={<Divider sx={{ borderColor: '#21262d' }} />}>
          {ALL_CHANNELS.map((ch) => {
            const enabled = !disabledChannels.has(ch);
            return (
              <Stack key={ch} direction="row" alignItems="center" spacing={2} sx={{ py: 1.5 }}>
                <Box sx={{ color: enabled ? '#a5b4fc' : '#6e7681', flexShrink: 0 }}>
                  {channelIcon(ch)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} color="#e6edf3">
                    {t(`prefs.channels.${ch}.label`)}
                  </Typography>
                  <Typography variant="caption" color="#8b949e">
                    {t(`prefs.channels.${ch}.description`)}
                  </Typography>
                </Box>
                <Switch checked={enabled} onChange={() => toggleChannel(ch)} />
              </Stack>
            );
          })}
        </Stack>
        <Box mt={2}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<MailOutlined fontSize="small" />}
            onClick={enablePush}
            sx={{ textTransform: 'none' }}
          >
            {t('prefs.actions.enablePush')}
          </Button>
        </Box>
      </Card>

      {/* Types card */}
      <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={700} color="#e6edf3" mb={0.5}>
          {t('prefs.typesCard.title')}
        </Typography>
        <Typography variant="caption" color="#8b949e" sx={{ display: 'block', mb: 2 }}>
          {t('prefs.typesCard.hint')}
        </Typography>
        <Stack divider={<Divider sx={{ borderColor: '#21262d' }} />}>
          {ALL_NOTIFICATION_TYPES.map((ty) => {
            const enabled = !optedOutTypes.has(ty);
            return (
              <Stack key={ty} direction="row" alignItems="flex-start" spacing={2} sx={{ py: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} mb={0.25}>
                    <Typography variant="body2" fontWeight={600} color="#e6edf3">
                      {t(`prefs.types.${ty}.label`)}
                    </Typography>
                    <Tooltip title={t(`prefs.types.${ty}.description`)} arrow>
                      <IconButton size="small" sx={{ color: '#6e7681', p: 0.25 }}>
                        <InfoOutlined sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography variant="caption" color="#8b949e">
                    {t(`prefs.types.${ty}.description`)}
                  </Typography>
                </Box>
                <Switch checked={enabled} onChange={() => toggleType(ty)} />
              </Stack>
            );
          })}
        </Stack>
      </Card>

      {/* Live preview / summary matrix */}
      <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={700} color="#e6edf3" mb={0.5}>
          {t('prefs.matrixCard.title')}
        </Typography>
        <Typography variant="caption" color="#8b949e" sx={{ display: 'block', mb: 2 }}>
          {t('prefs.matrixCard.hint')}
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `minmax(220px, 1fr) repeat(${ALL_CHANNELS.length}, auto)`,
              gap: 0,
              border: '1px solid #21262d',
              borderRadius: 1.5,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.02)' }}>
              <Typography variant="caption" color="#8b949e" fontWeight={700}>
                {t('prefs.matrixCard.typeHeader')}
              </Typography>
            </Box>
            {ALL_CHANNELS.map((ch) => (
              <Box
                key={ch}
                sx={{
                  p: 1,
                  bgcolor: 'rgba(255,255,255,0.02)',
                  textAlign: 'center',
                  minWidth: 70,
                }}
              >
                <Typography variant="caption" color="#8b949e" fontWeight={700}>
                  {t(`prefs.matrixCard.channelHeader.${ch}`)}
                </Typography>
              </Box>
            ))}
            {ALL_NOTIFICATION_TYPES.map((ty) => {
              const typeOn = !optedOutTypes.has(ty);
              return (
                <Box key={`row-${ty}`} sx={{ display: 'contents' }}>
                  <Box
                    sx={{
                      p: 1,
                      borderTop: '1px solid #21262d',
                      bgcolor: typeOn ? 'transparent' : 'rgba(110,118,129,0.06)',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color={typeOn ? '#c9d1d9' : '#6e7681'}
                      sx={{ textDecoration: typeOn ? 'none' : 'line-through' }}
                    >
                      {t(`prefs.types.${ty}.label`)}
                    </Typography>
                  </Box>
                  {ALL_CHANNELS.map((ch) => {
                    const channelOn = !disabledChannels.has(ch);
                    const delivers = typeOn && channelOn;
                    return (
                      <Box
                        key={`cell-${ty}-${ch}`}
                        sx={{
                          p: 1,
                          textAlign: 'center',
                          borderTop: '1px solid #21262d',
                          bgcolor: delivers ? 'rgba(16,185,129,0.06)' : 'rgba(110,118,129,0.04)',
                        }}
                      >
                        {delivers ? (
                          <CheckCircleRounded sx={{ color: '#10b981', fontSize: 18 }} />
                        ) : (
                          <CancelRounded sx={{ color: '#6e7681', fontSize: 18 }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        {dirty && (
          <Typography variant="caption" color="#fbbf24" sx={{ alignSelf: 'center', mr: 1 }}>
            {t('prefs.actions.dirty')}
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || !dirty}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {saving ? t('prefs.actions.saving') : t('prefs.actions.save')}
        </Button>
      </Box>
    </Stack>
  );
}
